/**
 * Readable 2D card chips for the HUD. The 3D cards on the felt are small and hard
 * to read across the table, so we mirror the player's own hand (in the ActionBar)
 * and the dealer's hand/score (DealerPanel) as crisp DOM cards.
 *
 * Pure projection of server state — own hole cards + others' public up-cards only
 * (CLAUDE.md rule 1). The dealer's hole card stays face-down until resolution.
 */

import type { Card, Suit } from '@blackjack/shared';
import { getHandValue, HOUSE_ID } from '@blackjack/shared';
import { useGameStore } from '../state/store';

const SUIT_SYMBOL: Record<Suit, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

const isRedSuit = (s: Suit): boolean => s === 'hearts' || s === 'diamonds';

/** One mini card. `undefined` renders a face-down back (the dealer's hole card). */
export function CardChip({ card }: { readonly card?: Card }) {
  if (!card) return <span className="cardchip cardchip--back" aria-hidden="true" />;
  return (
    <span className={`cardchip ${isRedSuit(card.suit) ? 'is-red' : ''}`}>
      <span className="cardchip__rank">{card.rank}</span>
      <span className="cardchip__suit">{SUIT_SYMBOL[card.suit]}</span>
    </span>
  );
}

/** A row of readable cards. */
export function HandView({ cards }: { readonly cards: readonly Card[] }) {
  return (
    <span className="handview">
      {cards.map((c, i) => (
        <CardChip key={`${c.rank}${c.suit}${i}`} card={c} />
      ))}
    </span>
  );
}

/**
 * The dealer's (house) hand + score. During play only the up-card is public, so
 * we show it + a face-down hole card + the up-card's value ("Dealer shows 10").
 * At resolution the full hand + total are revealed (matches the felt + results).
 */
export function DealerPanel() {
  const phase = useGameStore((s) => s.server.phase);
  const publicUpcards = useGameStore((s) => s.server.publicUpcards);
  const resolution = useGameStore((s) => s.server.resolution);

  const inRound =
    phase === 'DEALING' ||
    phase === 'PLAYER_ACTIONS' ||
    phase === 'DEALER_PLAY' ||
    phase === 'RESOLUTION' ||
    phase === 'TIEBREAK' ||
    phase === 'RESULT';
  if (!inRound) return null;

  const revealed = resolution?.hands.find((h) => h.id === HOUSE_ID);
  if (revealed) {
    const bust = revealed.value.total > 21;
    return (
      <div className="dealer-panel">
        <span className="dealer-panel__label">Dealer</span>
        <HandView cards={revealed.hand} />
        <span className={`dealer-panel__total numeric ${bust ? 'is-bust' : ''}`}>
          {bust ? `BUST ${revealed.value.total}` : revealed.value.total}
        </span>
      </div>
    );
  }

  const up = publicUpcards.find((v) => v.id === HOUSE_ID);
  if (!up || up.upcards.length === 0) return null;
  const showing = getHandValue(up.upcards).total;
  return (
    <div className="dealer-panel">
      <span className="dealer-panel__label">Dealer shows</span>
      <HandView cards={up.upcards} />
      <CardChip /> {/* face-down hole card */}
      <span className="dealer-panel__total numeric">{showing}</span>
    </div>
  );
}
